import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Download, Building2, Users as UsersIcon, Mail, Phone, Globe, MapPin, FileText, Receipt, TrendingUp, CheckCircle2, Clock, AlertCircle, Sparkles, Send, CalendarPlus } from 'lucide-react';
import api from '../api/client';
import CompanyFormDialog from '../components/CompanyFormDialog';
import FormDialog from '../components/FormDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import DetailSheet from '../components/DetailSheet';
import RowActions from '../components/RowActions';
import EmptyState from '../components/EmptyState';
import { SkeletonRow } from '../components/Skeleton';
import BulkActionsBar from '../components/BulkActionsBar';
import { exportToCsv } from '../utils/export';
import { sortNewestFirst } from '../utils/sort';
import { toast } from '../hooks/use-toast';
import { useLastSeen, useAutoMarkSeen } from '../hooks/useLastSeen';
import { useBulkSelectionKeyboard } from '../hooks/useBulkSelectionKeyboard';

export default function Entreprises() {
  useAutoMarkSeen('companies', 3000);
  const { isNew } = useLastSeen();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [create, setCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLearners, setDetailLearners] = useState([]);
  const [allLearners, setAllLearners] = useState([]);
  const [detailTab, setDetailTab] = useState('learners'); // 'learners' | 'billing'
  const [billing, setBilling] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [grouped, setGrouped] = useState(null); // { kind: 'invoice' | 'quote', ...initial }
  const [createLearnerOpen, setCreateLearnerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  const toggleSelect = (id) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const toggleSelectAll = (ids) => setSelected((s) => {
    const allSelected = ids.length > 0 && ids.every((id) => s.has(id));
    const n = new Set(s);
    if (allSelected) ids.forEach((id) => n.delete(id));
    else ids.forEach((id) => n.add(id));
    return n;
  });

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    const ids = Array.from(selected);
    try {
      await api.post('/bulk/delete', { collection: 'companies', ids });
      setItems((prev) => prev.filter((c) => !selected.has(c.id)));
      setSelected(new Set());
      toast({ title: 'Supprimées', description: `${ids.length} entreprise(s) supprimée(s).` });
    } catch {
      toast({ title: 'Erreur', description: 'Suppression groupée impossible.' });
      throw new Error();
    }
  };

  const bulkExport = () => {
    const rows = items.filter((c) => selected.has(c.id));
    exportToCsv('entreprises-selection', rows, ['id','name','siret','sector','contact_name','contact_email','contact_phone','learner_count']);
    toast({ title: 'Export CSV', description: `${rows.length} lignes exportées.` });
  };

  useEffect(() => {
    api.get('/companies').then(({ data }) => setItems(sortNewestFirst(data))).catch(() => {}).finally(() => setLoading(false));
    api.get('/learners').then(({ data }) => setAllLearners(data)).catch(() => {});
  }, []);

  const refreshDetailLearners = async () => {
    if (!detail) return;
    const { data } = await api.get(`/companies/${detail.id}/learners`);
    setDetailLearners(data);
  };

  useEffect(() => {
    if (detail) {
      setDetailTab('learners');
      setBilling(null);
      api.get(`/companies/${detail.id}/learners`).then(({ data }) => setDetailLearners(data)).catch(() => setDetailLearners([]));
    } else setDetailLearners([]);
  }, [detail]);

  useEffect(() => {
    if (detail && detailTab === 'billing' && !billing) {
      setBillingLoading(true);
      api.get(`/companies/${detail.id}/billing`)
        .then(({ data }) => setBilling(data))
        .catch(() => toast({ title: 'Erreur chargement facturation' }))
        .finally(() => setBillingLoading(false));
    }
  }, [detail, detailTab, billing]);

  const attachLearner = async (learnerId) => {
    try {
      await api.post(`/companies/${detail.id}/learners/${learnerId}/attach`);
      await refreshDetailLearners();
      // Reflect new count in list
      setItems((prev) => prev.map((c) => c.id === detail.id ? { ...c, learner_count: (c.learner_count || 0) + 1 } : c));
      // Keep allLearners in sync (mark attached)
      setAllLearners((prev) => prev.map((l) => l.id === learnerId ? { ...l, company_id: detail.id, company: detail.name } : l));
      toast({ title: 'Apprenant rattaché' });
    } catch {
      toast({ title: 'Erreur' });
    }
  };

  const handleCreateLearnerFromCompany = async (values) => {
    if (!detail) return;
    const payload = {
      ...values,
      company_id: detail.id,
      company: detail.name,
      status: values.status || 'Actif',
      tag: values.tag || 'Entreprise',
      progress: Number(values.progress) || 0,
    };
    const { data } = await api.post('/learners', payload);
    setDetailLearners((prev) => [data, ...prev]);
    setAllLearners((prev) => [data, ...prev]);
    setItems((prev) => prev.map((c) => c.id === detail.id ? { ...c, learner_count: (c.learner_count || 0) + 1 } : c));
    toast({ title: 'Apprenant créé', description: `${data.firstName} ${data.lastName} rattaché à ${detail.name}` });
  };

  const handleCreate = async (values) => {
    const { data } = await api.post('/companies', values);
    setItems((prev) => sortNewestFirst([...prev, { ...data, learner_count: 0 }]));
    toast({ title: 'Entreprise créée', description: data.name });
  };
  const handleUpdate = async (values) => {
    const { data } = await api.put(`/companies/${editing.id}`, values);
    setItems((prev) => prev.map((c) => c.id === data.id ? { ...c, ...data } : c));
    toast({ title: 'Entreprise modifiée', description: data.name });
  };
  const handleDelete = async () => {
    await api.delete(`/companies/${deleting.id}`);
    setItems((prev) => prev.filter((c) => c.id !== deleting.id));
    toast({ title: 'Entreprise supprimée' });
  };

  const filtered = items.filter((c) => {
    const q = search.toLowerCase();
    return `${c.name} ${c.siret} ${c.contact_name} ${c.sector}`.toLowerCase().includes(q);
  });

  // ⌘A / Suppr / Échap keyboard shortcuts — selects the whole filtered list
  useBulkSelectionKeyboard({
    items: filtered,
    selected,
    setSelected,
    onDelete: () => setBulkDeleteOpen(true),
  });

  return (
    <div className="space-y-5">
      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8E8FA5]" />
          <input
            data-testid="companies-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher une entreprise, un SIRET, un contact…"
            className="w-full h-10 pl-10 pr-4 rounded-xl bg-white border border-[#EFF0F6] text-[13.5px] focus:border-[#C7D2FE] outline-none"
          />
        </div>
        <div className="flex gap-2 md:ml-auto">
          <button
            onClick={() => exportToCsv('entreprises', filtered, ['id','name','siret','sector','contact_name','contact_email','contact_phone','learner_count'])}
            className="h-10 px-3.5 rounded-xl bg-white border border-[#EFF0F6] text-[13px] font-medium text-[#4A4B63] flex items-center gap-2 hover:border-[#C7D2FE]"
          >
            <Download className="w-4 h-4" /> Exporter
          </button>
          <button data-testid="companies-create-btn" onClick={() => setCreate(true)} className="h-10 px-4 rounded-xl brand-gradient text-white text-[13px] font-semibold flex items-center gap-2 shadow-sm hover:opacity-95">
            <Plus className="w-4 h-4" /> Nouvelle entreprise
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Entreprises', value: items.length, color: '#4F46E5' },
          { label: 'Apprenants rattachés', value: items.reduce((a, c) => a + (c.learner_count || 0), 0), color: '#14B8A6' },
          { label: 'Secteurs distincts', value: new Set(items.map((c) => c.sector).filter(Boolean)).size, color: '#F59E0B' },
          { label: 'Avec email de contact', value: items.filter((c) => c.contact_email).length, color: '#3B82F6' },
        ].map((s, i) => (
          <div key={i} className="soft-card p-4">
            <div className="text-[12px] text-[#8E8FA5] font-medium">{s.label}</div>
            <div className="text-[22px] font-bold mt-0.5" style={{ color: s.color, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{s.value}</div>
          </div>
        ))}
      </div>

      <div className="soft-card overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="text-left text-[11.5px] font-semibold uppercase tracking-wider text-[#8E8FA5] border-b border-[#EFF0F6]">
              <th className="py-3.5 pl-5 pr-1 w-10">
                <input
                  data-testid="companies-select-all"
                  type="checkbox"
                  checked={filtered.length > 0 && filtered.every((c) => selected.has(c.id))}
                  onChange={() => toggleSelectAll(filtered.map((c) => c.id))}
                  className="w-4 h-4 rounded accent-[#4F46E5] cursor-pointer"
                />
              </th>
              <th className="py-3.5 px-5">Entreprise</th>
              <th className="py-3.5 px-5">Secteur</th>
              <th className="py-3.5 px-5">Contact</th>
              <th className="py-3.5 px-5">Apprenants</th>
              <th className="py-3.5 px-5"></th>
            </tr>
          </thead>
          <tbody>
            {loading && Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} cols={6} />)}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={6}><EmptyState icon={Building2} title="Aucune entreprise" description="Créez une fiche entreprise pour regrouper plusieurs apprenants sous un même client." action={<button onClick={() => setCreate(true)} className="h-10 px-4 rounded-xl brand-gradient text-white text-[13px] font-semibold">Nouvelle entreprise</button>} /></td></tr>
            )}
            {!loading && filtered.map((c) => {
              const itemIsNew = isNew('companies', c.created_at);
              return (
              <tr key={c.id} data-testid={`company-row-${c.id}`} onClick={() => navigate(`/app/entreprises/${c.id}`)} className={`data-row border-b border-[#F3F4F8] last:border-0 cursor-pointer transition-colors ${selected.has(c.id) ? 'bg-[#EEF2FF]' : itemIsNew ? 'bg-[#FAF6FF] hover:bg-[#EEF2FF]' : 'hover:bg-[#FAFAFE]'}`}>
                <td className="py-4 pl-5 pr-1 w-10" onClick={(e) => e.stopPropagation()}>
                  <input
                    data-testid={`company-select-${c.id}`}
                    type="checkbox"
                    checked={selected.has(c.id)}
                    onChange={() => toggleSelect(c.id)}
                    className="w-4 h-4 rounded accent-[#4F46E5] cursor-pointer"
                  />
                </td>
                <td className="py-4 px-5">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-[#EEF2FF] text-[#4F46E5] font-semibold flex items-center justify-center text-[13px]">
                      {c.name?.slice(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-[13.5px] font-semibold text-[#1A1B2E]">{c.name}</span>
                        {itemIsNew && <span data-testid={`new-badge-${c.id}`} className="inline-flex items-center px-1.5 py-0.5 rounded bg-[#4F46E5] text-white text-[9.5px] font-bold tracking-wide">NOUVEAU</span>}
                        {c.opco_code && (
                          <span data-testid={`company-opco-badge-${c.id}`} className="inline-flex items-center px-1.5 py-0.5 rounded bg-[#E6FBF7] text-[#0F766E] text-[10px] font-bold tracking-wide" title={c.opco_name}>
                            {c.opco_code}
                          </span>
                        )}
                      </div>
                      <div className="text-[11.5px] text-[#8E8FA5] font-mono">{c.siret || '—'}</div>
                    </div>
                  </div>
                </td>
                <td className="py-4 px-5 text-[13px] text-[#4A4B63]">{c.sector || '—'}</td>
                <td className="py-4 px-5">
                  <div className="text-[12.5px] text-[#4A4B63]">{c.contact_name || <span className="italic text-[#8E8FA5]">Non renseigné</span>}</div>
                  {c.contact_email && <div className="text-[11.5px] text-[#8E8FA5] flex items-center gap-1"><Mail className="w-3 h-3" /> {c.contact_email}</div>}
                </td>
                <td className="py-4 px-5">
                  <span className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#1A1B2E]">
                    <UsersIcon className="w-3.5 h-3.5 text-[#4F46E5]" />
                    {c.learner_count || 0}
                  </span>
                </td>
                <td className="py-4 px-5">
                  <RowActions onView={() => navigate(`/app/entreprises/${c.id}`)} onEdit={() => setEditing(c)} onDelete={() => setDeleting(c)} />
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <CompanyFormDialog open={create} onOpenChange={setCreate} onSubmit={handleCreate} />
      <CompanyFormDialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)} initialValues={editing} onSubmit={handleUpdate} />
      <FormDialog
        open={createLearnerOpen}
        onOpenChange={setCreateLearnerOpen}
        title={detail ? `Nouvel apprenant · ${detail.name}` : 'Nouvel apprenant'}
        description="L'apprenant sera automatiquement rattaché à cette entreprise."
        fields={[
          { name: 'firstName', label: 'Prénom', required: true },
          { name: 'lastName', label: 'Nom', required: true },
          { name: 'email', label: 'Email', type: 'email', required: true, full: true },
          { name: 'phone', label: 'Téléphone' },
          { name: 'tag', label: 'Catégorie', type: 'select', options: ['Entreprise', 'Premium'], required: true, defaultValue: 'Entreprise' },
          { name: 'status', label: 'Statut', type: 'select', options: ['Actif', 'En pause', 'Terminé'], defaultValue: 'Actif' },
        ]}
        onSubmit={handleCreateLearnerFromCompany}
      />
      <ConfirmDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)} title="Supprimer cette entreprise ?" description={deleting ? `${deleting.name} sera supprimée. Les apprenants rattachés seront détachés (non supprimés).` : ''} onConfirm={handleDelete} />
      <BulkActionsBar
        count={selected.size}
        onClear={() => setSelected(new Set())}
        onDelete={() => setBulkDeleteOpen(true)}
        onExport={bulkExport}
      />
      <ConfirmDialog
        open={bulkDeleteOpen}
        onOpenChange={setBulkDeleteOpen}
        title={`Supprimer ${selected.size} entreprise${selected.size > 1 ? 's' : ''} ?`}
        description={`Cette action est irréversible. Les apprenants rattachés seront détachés (non supprimés). ${selected.size} fiche${selected.size > 1 ? 's' : ''} sera${selected.size > 1 ? 'ont' : ''} définitivement supprimée${selected.size > 1 ? 's' : ''}.`}
        confirmLabel={`Supprimer ${selected.size} entreprise${selected.size > 1 ? 's' : ''}`}
        onConfirm={bulkDelete}
      />
      <DetailSheet
        open={!!detail}
        onOpenChange={(o) => !o && setDetail(null)}
        title={detail?.name}
        subtitle={detail?.siret}
        rows={detail ? [
          { label: 'SIRET', value: detail.siret || '—' },
          { label: 'SIREN', value: detail.siren || '—' },
          { label: 'TVA intracom.', value: detail.tva_intra || '—' },
          { label: 'IDCC', value: (detail.liste_idcc || []).map((c) => `IDCC ${c}`).join(', ') || '—' },
          { label: 'OPCO', value: detail.opco_code ? `${detail.opco_code} · ${detail.opco_name || ''}` : '—' },
          { label: 'Forme juridique', value: detail.legal_form || '—' },
          { label: 'Capital social', value: detail.capital_social ? `${Number(detail.capital_social).toLocaleString('fr-FR')} €` : '—' },
          { label: 'Dirigeant', value: [detail.leader_name, detail.leader_role].filter(Boolean).join(' · ') || '—' },
          { label: 'Code NAF', value: detail.naf_code || '—' },
          { label: 'Secteur', value: detail.sector || '—' },
          { label: 'Effectif', value: detail.employees_count || '—' },
          { label: 'Adresse', value: detail.address || '—' },
          { label: 'Code postal · Ville', value: [detail.postal_code, detail.city].filter(Boolean).join(' ') || '—' },
          { label: 'Contact', value: detail.contact_name || '—' },
          { label: 'Email', value: detail.contact_email || '—' },
          { label: 'Téléphone', value: detail.contact_phone || '—' },
          { label: 'Site web', value: detail.website || '—' },
          { label: 'Apprenants', value: detailLearners.length },
        ] : []}
      >
        {detail && (
          <div className="mt-4 pt-4 border-t border-[#EFF0F6]">
            {/* Tabs */}
            <div className="flex items-center gap-1 mb-4 p-1 bg-[#F6F7FB] rounded-xl">
              <button
                data-testid="company-tab-learners"
                onClick={() => setDetailTab('learners')}
                className={`flex-1 h-9 px-3 rounded-lg text-[13px] font-semibold flex items-center justify-center gap-1.5 transition-colors ${detailTab === 'learners' ? 'bg-white text-[#1A1B2E] shadow-sm' : 'text-[#4A4B63] hover:text-[#1A1B2E]'}`}
              >
                <UsersIcon className="w-3.5 h-3.5" /> Apprenants
                <span className="ml-1 text-[11px] font-bold text-[#8E8FA5]">{detailLearners.length}</span>
              </button>
              <button
                data-testid="company-tab-billing"
                onClick={() => setDetailTab('billing')}
                className={`flex-1 h-9 px-3 rounded-lg text-[13px] font-semibold flex items-center justify-center gap-1.5 transition-colors ${detailTab === 'billing' ? 'bg-white text-[#1A1B2E] shadow-sm' : 'text-[#4A4B63] hover:text-[#1A1B2E]'}`}
              >
                <Receipt className="w-3.5 h-3.5" /> Contrats &amp; facturation
              </button>
              <button
                data-testid="company-tab-preinscriptions"
                onClick={() => setDetailTab('preinscriptions')}
                className={`flex-1 h-9 px-3 rounded-lg text-[13px] font-semibold flex items-center justify-center gap-1.5 transition-colors ${detailTab === 'preinscriptions' ? 'bg-white text-[#1A1B2E] shadow-sm' : 'text-[#4A4B63] hover:text-[#1A1B2E]'}`}
              >
                <CalendarPlus className="w-3.5 h-3.5" /> Pré-inscriptions
                <span className="ml-1 text-[11px] font-bold text-[#8E8FA5]">{(detail?.preinscriptions || []).length}</span>
              </button>
              <button
                data-testid="company-tab-conventions"
                onClick={() => setDetailTab('conventions')}
                className={`flex-1 h-9 px-3 rounded-lg text-[13px] font-semibold flex items-center justify-center gap-1.5 transition-colors ${detailTab === 'conventions' ? 'bg-white text-[#1A1B2E] shadow-sm' : 'text-[#4A4B63] hover:text-[#1A1B2E]'}`}
              >
                <FileText className="w-3.5 h-3.5" /> Conventions
                <span className="ml-1 text-[11px] font-bold text-[#0F766E]">{(detail?.signed_conventions || []).length}</span>
              </button>
              <button
                data-testid="company-tab-convocations"
                onClick={() => setDetailTab('convocations')}
                className={`flex-1 h-9 px-3 rounded-lg text-[13px] font-semibold flex items-center justify-center gap-1.5 transition-colors ${detailTab === 'convocations' ? 'bg-white text-[#1A1B2E] shadow-sm' : 'text-[#4A4B63] hover:text-[#1A1B2E]'}`}
              >
                <Send className="w-3.5 h-3.5" /> Convocations
                <span className="ml-1 text-[11px] font-bold text-[#0F766E]">{(detail?.sent_convocations || []).length}</span>
              </button>
            </div>

            {detailTab === 'learners' && (
              <>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-[13.5px] font-semibold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    Apprenants rattachés ({detailLearners.length})
                  </h4>
                  <button
                    type="button"
                    data-testid="company-create-learner-btn"
                    onClick={() => setCreateLearnerOpen(true)}
                    className="h-8 px-3 rounded-lg brand-gradient text-white text-[12px] font-semibold inline-flex items-center gap-1.5 shadow-sm"
                  >
                    <Plus className="w-3.5 h-3.5" /> Nouvel apprenant
                  </button>
                </div>
                <div className="space-y-2 max-h-[280px] overflow-y-auto">
                  {detailLearners.length === 0 && <div className="text-[12.5px] text-[#8E8FA5] italic">Aucun apprenant rattaché à cette entreprise.</div>}
                  {detailLearners.map((l) => (
                    <div key={l.id} data-testid={`company-learner-${l.id}`} className="flex items-center gap-2 p-2.5 rounded-lg bg-[#FAFAFE] border border-[#EFF0F6]">
                      <div className="w-9 h-9 rounded-full brand-gradient text-white font-semibold flex items-center justify-center text-[12px]">
                        {l.firstName?.[0]}{l.lastName?.[0]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-semibold text-[#1A1B2E] truncate">{l.firstName} {l.lastName}</div>
                        <div className="text-[11.5px] text-[#8E8FA5] truncate">{l.email}</div>
                      </div>
                      <span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-[#EEF2FF] text-[#4F46E5]">{l.status}</span>
                    </div>
                  ))}
                </div>
              </>
            )}

            {detailTab === 'billing' && (
              <BillingTab
                billing={billing}
                loading={billingLoading}
                learners={detailLearners}
                onCreateGrouped={() => { setGrouped({ kind: 'invoice', company: detail, learnerSnapshot: [...detailLearners], learner_ids: detailLearners.map((l) => l.id), amount_per_learner: 0, description: '', due_date: '' }); setDetail(null); }}
                onCreateQuote={() => {
                  // Navigate to /app/devis and auto-open "Nouveau devis" with the company + learners prefilled
                  const prefill = {
                    company_id: detail.id,
                    learner_ids: detailLearners.map((l) => l.id),
                  };
                  setDetail(null);
                  navigate('/app/devis', { state: { prefillCreate: prefill } });
                }}
              />
            )}

            {detailTab === 'preinscriptions' && (
              <div data-testid="company-preinscriptions-list">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-[13.5px] font-semibold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    Pré-inscriptions archivées ({(detail?.preinscriptions || []).length})
                  </h4>
                </div>
                {(!detail.preinscriptions || detail.preinscriptions.length === 0) && (
                  <div className="rounded-xl bg-[#FAFAFE] border border-dashed border-[#EFF0F6] p-6 text-center text-[12.5px] text-[#8E8FA5]">
                    Aucune pré-inscription finalisée pour cette entreprise pour le moment.
                    <div className="mt-1 text-[11.5px]">Une pré-inscription est archivée automatiquement dès qu'une session est créée depuis un devis signé.</div>
                  </div>
                )}
                <div className="space-y-2 max-h-[340px] overflow-y-auto">
                    {(detail.preinscriptions || [])
                      .slice()
                      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
                      .map((p) => (
                        <div
                          key={p.id}
                          data-testid={`company-preinscription-${p.id}`}
                          role="button"
                          tabIndex={0}
                          onClick={() => { if (p.session_id) { setDetail(null); navigate(`/app/sessions?focus=${p.session_id}`); } }}
                          onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && p.session_id) { setDetail(null); navigate(`/app/sessions?focus=${p.session_id}`); } }}
                          className="flex items-center gap-3 p-3 rounded-xl bg-[#FAFAFE] border border-[#EFF0F6] hover:border-[#C7D2FE] hover:bg-white transition-colors cursor-pointer"
                        >
                        <div className="w-10 h-10 rounded-xl bg-[#EEF2FF] text-[#4F46E5] flex items-center justify-center shrink-0">
                          <CalendarPlus className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] font-semibold text-[#1A1B2E] truncate">{p.session_title || p.session_id}</div>
                          <div className="text-[11.5px] text-[#6B6C85] mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                            {p.startDate && (
                              <span className="inline-flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {new Date(p.startDate).toLocaleDateString('fr-FR')}
                                {p.endDate && p.endDate !== p.startDate ? ` → ${new Date(p.endDate).toLocaleDateString('fr-FR')}` : ''}
                              </span>
                            )}
                            {typeof p.learner_count === 'number' && (
                              <span className="inline-flex items-center gap-1"><UsersIcon className="w-3 h-3" /> {p.learner_count} apprenant{p.learner_count > 1 ? 's' : ''}</span>
                            )}
                            {p.quote_id && (
                              <span className="inline-flex items-center gap-1"><FileText className="w-3 h-3" /> Devis {p.quote_id}</span>
                            )}
                          </div>
                          <div className="text-[10.5px] text-[#8E8FA5] mt-0.5">
                            Archivée le {p.created_at ? new Date(p.created_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                          </div>
                        </div>
                        <button
                          data-testid={`company-preinscription-pdf-${p.id}`}
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              const res = await api.get(`/preinscriptions/${p.id}/pdf`, { responseType: 'blob' });
                              const url = URL.createObjectURL(res.data);
                              window.open(url, '_blank', 'noopener');
                              setTimeout(() => URL.revokeObjectURL(url), 60_000);
                            } catch (err) {
                              toast({ title: 'Erreur', description: 'PDF introuvable' });
                            }
                          }}
                          title="Télécharger le PDF de la pré-inscription"
                          className="w-9 h-9 rounded-lg bg-[#EEF2FF] text-[#4F46E5] hover:bg-[#EDE8FF] flex items-center justify-center shrink-0"
                        >
                          <Download className="w-3.5 h-3.5" />
                        </button>
                      </div>
                  ))}
                </div>
              </div>
            )}

            {detailTab === 'conventions' && (
              <div data-testid="company-conventions-list">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-[13.5px] font-semibold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    Conventions de formation signées ({(detail?.signed_conventions || []).length})
                  </h4>
                </div>
                {(!detail.signed_conventions || detail.signed_conventions.length === 0) && (
                  <div className="rounded-xl bg-[#FAFAFE] border border-dashed border-[#EFF0F6] p-6 text-center text-[12.5px] text-[#8E8FA5]">
                    Aucune convention signée pour cette entreprise pour le moment.
                    <div className="mt-1 text-[11.5px]">Une convention est archivée ici dès qu'elle est signée par le client via le lien public.</div>
                  </div>
                )}
                <div className="space-y-2 max-h-[340px] overflow-y-auto">
                  {(detail.signed_conventions || [])
                    .slice()
                    .sort((a, b) => new Date(b.signed_at || b.created_at || 0) - new Date(a.signed_at || a.created_at || 0))
                    .map((c) => (
                      <div
                        key={c.id}
                        data-testid={`company-convention-${c.id}`}
                        role="button"
                        tabIndex={0}
                        onClick={() => { if (c.session_id) { setDetail(null); navigate(`/app/sessions?focus=${c.session_id}`); } }}
                        onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && c.session_id) { setDetail(null); navigate(`/app/sessions?focus=${c.session_id}`); } }}
                        className="flex items-center gap-3 p-3 rounded-xl bg-[#FAFAFE] border border-[#EFF0F6] hover:border-[#A7F3D0] hover:bg-white transition-colors cursor-pointer"
                      >
                        <div className="w-10 h-10 rounded-xl bg-[#E6FBF7] text-[#0F766E] flex items-center justify-center shrink-0">
                          <FileText className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] font-semibold text-[#1A1B2E] truncate">
                            Convention {c.session_id || c.id}
                          </div>
                          <div className="text-[11.5px] text-[#6B6C85] mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                            <span className="inline-flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3 text-[#0F766E]" />
                              Signée par <strong>{c.signatory_name}</strong>
                            </span>
                            {c.signed_at && (
                              <span className="inline-flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {new Date(c.signed_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
                              </span>
                            )}
                            {c.size && (
                              <span className="text-[10.5px] text-[#8E8FA5]">{Math.round(c.size / 1024)} Ko</span>
                            )}
                          </div>
                        </div>
                        <button
                          data-testid={`company-convention-pdf-${c.id}`}
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              const res = await api.get(`/companies/${detail.id}/signed-conventions/${c.id}/pdf`, { responseType: 'blob' });
                              const url = URL.createObjectURL(res.data);
                              window.open(url, '_blank', 'noopener');
                              setTimeout(() => URL.revokeObjectURL(url), 60_000);
                            } catch (err) {
                              toast({ title: 'Erreur', description: 'PDF introuvable' });
                            }
                          }}
                          title="Télécharger la convention signée"
                          className="w-9 h-9 rounded-lg bg-[#E6FBF7] text-[#0F766E] hover:bg-[#CCF5EC] flex items-center justify-center shrink-0"
                        >
                          <Download className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {detailTab === 'convocations' && (
              <div data-testid="company-convocations-list">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-[13.5px] font-semibold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    Convocations envoyées ({(detail?.sent_convocations || []).length})
                  </h4>
                </div>
                {(!detail.sent_convocations || detail.sent_convocations.length === 0) && (
                  <div className="rounded-xl bg-[#FAFAFE] border border-dashed border-[#EFF0F6] p-6 text-center text-[12.5px] text-[#8E8FA5]">
                    Aucune convocation envoyée pour cette entreprise.
                    <div className="mt-1 text-[11.5px]">Les convocations sont archivées ici dès qu'elles sont envoyées aux apprenants.</div>
                  </div>
                )}
                <div className="space-y-2 max-h-[340px] overflow-y-auto">
                  {(detail.sent_convocations || [])
                    .slice()
                    .sort((a, b) => new Date(b.sent_at || b.created_at || 0) - new Date(a.sent_at || a.created_at || 0))
                    .map((c) => (
                      <div
                        key={c.id}
                        data-testid={`company-convocation-${c.id}`}
                        role="button"
                        tabIndex={0}
                        onClick={() => { if (c.session_id) { setDetail(null); navigate(`/app/sessions?focus=${c.session_id}`); } }}
                        onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && c.session_id) { setDetail(null); navigate(`/app/sessions?focus=${c.session_id}`); } }}
                        className="flex items-center gap-3 p-3 rounded-xl bg-[#FAFAFE] border border-[#EFF0F6] hover:border-[#A7F3D0] hover:bg-white transition-colors cursor-pointer"
                      >
                        <div className="w-10 h-10 rounded-xl bg-[#E6FBF7] text-[#0F766E] flex items-center justify-center shrink-0">
                          <Send className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] font-semibold text-[#1A1B2E] truncate">
                            Convocation {c.session_id || c.id}
                          </div>
                          <div className="text-[11.5px] text-[#6B6C85] mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                            <span className="inline-flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3 text-[#0F766E]" /> {c.sent_count || 0} apprenant{(c.sent_count || 0) > 1 ? 's' : ''}
                            </span>
                            {c.sent_at && (
                              <span className="inline-flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {new Date(c.sent_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
                              </span>
                            )}
                            {c.with_welcome_booklet && (
                              <span className="text-[10.5px] text-[#0F766E] font-semibold">+ livret d'accueil</span>
                            )}
                          </div>
                        </div>
                        <button
                          data-testid={`company-convocation-pdf-${c.id}`}
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              const res = await api.get(`/companies/${detail.id}/sent-convocations/${c.id}/pdf`, { responseType: 'blob' });
                              const url = URL.createObjectURL(res.data);
                              window.open(url, '_blank', 'noopener');
                              setTimeout(() => URL.revokeObjectURL(url), 60_000);
                            } catch { toast({ title: 'Erreur', description: 'PDF introuvable' }); }
                          }}
                          title="Télécharger la convocation"
                          className="w-9 h-9 rounded-lg bg-[#E6FBF7] text-[#0F766E] hover:bg-[#CCF5EC] flex items-center justify-center shrink-0"
                        >
                          <Download className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {detail.notes && (
              <div className="mt-4 p-3 rounded-lg bg-[#FEFBEA] border border-[#FDEFC3]">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-[#92782C] mb-1">Notes</div>
                <div className="text-[12.5px] text-[#4A4B63] whitespace-pre-wrap">{detail.notes}</div>
              </div>
            )}
            {detailTab === 'learners' && (
              <details className="mt-4 group">
                <summary className="cursor-pointer h-9 px-3 rounded-lg bg-[#EEF2FF] text-[#4F46E5] text-[12.5px] font-semibold flex items-center justify-center gap-1.5 hover:bg-[#EDE8FF] list-none">
                  + Rattacher un apprenant existant
                </summary>
                <div className="mt-2 max-h-[200px] overflow-y-auto space-y-1 border border-[#EFF0F6] rounded-lg p-2">
                  {(() => {
                    // Pre-compute attached IDs once and filter learners O(n)
                    // instead of doing two O(n²) filter+find scans on every
                    // render (could ddos the UI with hundreds of learners).
                    const attached = new Set((detailLearners || []).map((dl) => dl.id));
                    const candidates = allLearners.filter((l) => !attached.has(l.id));
                    if (candidates.length === 0) {
                      return (
                        <div className="text-[12px] text-[#8E8FA5] italic px-2 py-1">Tous les apprenants sont déjà rattachés.</div>
                      );
                    }
                    return candidates.map((l) => (
                      <button
                        key={l.id}
                        data-testid={`attach-learner-${l.id}`}
                        onClick={() => attachLearner(l.id)}
                        className="w-full text-left px-2 py-1.5 rounded hover:bg-[#EEF2FF] text-[12.5px] flex items-center gap-2"
                      >
                        <span className="font-medium text-[#1A1B2E]">{l.firstName} {l.lastName}</span>
                        <span className="text-[11px] text-[#8E8FA5]">· {l.company || 'sans entreprise'}</span>
                      </button>
                    ));
                  })()}
                </div>
              </details>
            )}
          </div>
        )}
      </DetailSheet>

      {/* Grouped invoice/quote dialog. Snapshots are captured at open time so the DetailSheet
          can safely close without losing the company + learners context (better UX: user sees
          the full-width dialog instead of it hiding behind the right panel). */}
      {grouped && grouped.company && (
        <GroupedDocDialog
          open={!!grouped}
          kind={grouped.kind}
          onClose={() => setGrouped(null)}
          company={grouped.company}
          learners={grouped.learnerSnapshot || []}
          initial={grouped}
          onCreated={(doc) => {
            setGrouped(null);
            setBilling(null);
            const label = grouped.kind === 'quote' ? 'Devis' : 'Facture';
            const emailSuffix = doc.email_sent_to ? ` · 📧 envoyé à ${doc.email_sent_to}` : '';
            toast({
              title: `${label} groupé(e) créé(e)`,
              description: `${doc.id} · ${doc.amount.toLocaleString('fr-FR')} € TTC${emailSuffix}`,
            });
          }}
        />
      )}
    </div>
  );
}

/* ==================== BillingTab ==================== */

const INVOICE_STATUS_META = {
  'Payée':      { icon: CheckCircle2, style: 'bg-[#E6FBF7] text-[#0F766E]' },
  'En attente': { icon: Clock,        style: 'bg-[#FEF3E7] text-[#B45309]' },
  'En retard':  { icon: AlertCircle,  style: 'bg-[#FEF2F2] text-[#B91C1C]' },
};
const QUOTE_STATUS_STYLE = {
  'Brouillon':   'bg-[#F3F4F8] text-[#4A4B63]',
  'Envoyé':      'bg-[#E0F2FE] text-[#0369A1]',
  'Accepté':     'bg-[#E6FBF7] text-[#0F766E]',
  'Refusé':      'bg-[#FEF2F2] text-[#B91C1C]',
  'Négociation': 'bg-[#FDF4FF] text-[#A21CAF]',
};

function BillingTab({ billing, loading, learners, onCreateGrouped, onCreateQuote }) {
  if (loading) {
    return <div className="py-10 text-center text-[12.5px] text-[#8E8FA5]">Chargement de la facturation…</div>;
  }
  if (!billing) return null;
  const t = billing.totals || {};
  const fmt = (n) => `${(n || 0).toLocaleString('fr-FR')} €`;

  return (
    <div className="space-y-4">
      {/* KPIs billing */}
      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-xl p-3 bg-[#E6FBF7] border border-[#A7F3D0]">
          <div className="text-[10.5px] font-semibold uppercase tracking-wider text-[#0F766E]">Encaissé</div>
          <div className="text-[17px] font-bold text-[#0F766E] mt-0.5" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }} data-testid="billing-ca-paid">{fmt(t.ca_paid)}</div>
        </div>
        <div className="rounded-xl p-3 bg-[#FEF3E7] border border-[#FDE68A]">
          <div className="text-[10.5px] font-semibold uppercase tracking-wider text-[#B45309]">En attente</div>
          <div className="text-[17px] font-bold text-[#B45309] mt-0.5" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }} data-testid="billing-ca-pending">{fmt(t.ca_pending)}</div>
        </div>
        <div className="rounded-xl p-3 bg-[#FEF2F2] border border-[#FECACA]">
          <div className="text-[10.5px] font-semibold uppercase tracking-wider text-[#B91C1C]">En retard</div>
          <div className="text-[17px] font-bold text-[#B91C1C] mt-0.5" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }} data-testid="billing-ca-overdue">{fmt(t.ca_overdue)}</div>
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-[12px] text-[#8E8FA5]">
          <span className="font-semibold text-[#1A1B2E]">{t.invoice_count}</span> facture(s) · <span className="font-semibold text-[#1A1B2E]">{t.quote_count}</span> devis · Total CA <span className="font-semibold text-[#1A1B2E]">{fmt(t.ca_total)}</span>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="billing-create-quote-btn"
            disabled={!learners.length}
            onClick={onCreateQuote}
            className="h-9 px-3 rounded-lg bg-white border border-[#C7D2FE] text-[#4F46E5] text-[12.5px] font-semibold flex items-center gap-1.5 hover:bg-[#EEF2FF] disabled:opacity-50 disabled:cursor-not-allowed"
            title={learners.length ? '' : "Rattachez au moins un apprenant d'abord"}
          >
            <FileText className="w-3.5 h-3.5" /> Créer un devis
          </button>
          <button
            data-testid="billing-create-grouped-btn"
            disabled={!learners.length}
            onClick={onCreateGrouped}
            className="h-9 px-3 rounded-lg brand-gradient text-white text-[12.5px] font-semibold flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
            title={learners.length ? '' : "Rattachez au moins un apprenant d'abord"}
          >
            <Sparkles className="w-3.5 h-3.5" /> Facture groupée
          </button>
        </div>
      </div>

      {/* Quotes */}
      <div>
        <div className="text-[11.5px] font-semibold uppercase tracking-wider text-[#8E8FA5] mb-2 flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> Devis ({billing.quotes.length})</div>
        {billing.quotes.length === 0 && <div className="text-[12.5px] italic text-[#8E8FA5]">Aucun devis.</div>}
        <div className="space-y-1.5">
          {billing.quotes.slice(0, 8).map((q) => (
            <div key={q.id} data-testid={`billing-quote-${q.id}`} className="flex items-center gap-2 py-2 px-2.5 rounded-lg border border-[#EFF0F6] bg-white">
              <div className="w-7 h-7 rounded-lg bg-[#EEF2FF] text-[#4F46E5] flex items-center justify-center text-[10.5px] font-semibold shrink-0">D</div>
              <div className="flex-1 min-w-0">
                <div className="text-[12.5px] font-mono font-semibold text-[#1A1B2E]">{q.id}</div>
                <div className="text-[11px] text-[#8E8FA5]">{q.date}</div>
              </div>
              <span className="text-[12px] font-semibold text-[#1A1B2E]">{Number(q.amount || 0).toLocaleString('fr-FR')} €</span>
              <span className={`text-[10.5px] font-semibold px-2 py-0.5 rounded ${QUOTE_STATUS_STYLE[q.status] || 'bg-[#F3F4F8] text-[#4A4B63]'}`}>{q.status || '—'}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Invoices */}
      <div>
        <div className="text-[11.5px] font-semibold uppercase tracking-wider text-[#8E8FA5] mb-2 flex items-center gap-1"><Receipt className="w-3.5 h-3.5" /> Factures ({billing.invoices.length})</div>
        {billing.invoices.length === 0 && <div className="text-[12.5px] italic text-[#8E8FA5]">Aucune facture.</div>}
        <div className="space-y-1.5">
          {billing.invoices.slice(0, 10).map((f) => {
            const meta = INVOICE_STATUS_META[f.status] || { icon: Clock, style: 'bg-[#F3F4F8] text-[#4A4B63]' };
            const Ic = meta.icon;
            return (
              <div key={f.id} data-testid={`billing-invoice-${f.id}`} className="flex items-center gap-2 py-2 px-2.5 rounded-lg border border-[#EFF0F6] bg-white">
                <div className="w-7 h-7 rounded-lg bg-[#E6FBF7] text-[#0F766E] flex items-center justify-center text-[10.5px] font-semibold shrink-0">F</div>
                <div className="flex-1 min-w-0">
                  <div className="text-[12.5px] font-mono font-semibold text-[#1A1B2E]">{f.id}</div>
                  <div className="text-[11px] text-[#8E8FA5]">
                    Éch. {f.dueDate || f.date}
                    {f.email_sent_at && <span className="ml-1.5 text-[#0F766E]">· 📧 envoyé</span>}
                  </div>
                </div>
                <span className="text-[12px] font-semibold text-[#1A1B2E]">{Number(f.amount || 0).toLocaleString('fr-FR')} €</span>
                <span className={`text-[10.5px] font-semibold px-2 py-0.5 rounded inline-flex items-center gap-1 ${meta.style}`}>
                  <Ic className="w-3 h-3" /> {f.status || '—'}
                </span>
                <button
                  data-testid={`send-invoice-${f.id}`}
                  title={f.email_sent_at ? 'Renvoyer le PDF au client' : 'Envoyer le PDF au client'}
                  onClick={async () => {
                    try {
                      const { data } = await api.post(`/invoices/${f.id}/send-to-client`, {});
                      toast({ title: 'Email envoyé', description: `${f.id} → ${data.sent_to}` });
                    } catch (err) {
                      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Envoi impossible' });
                    }
                  }}
                  className="w-7 h-7 rounded-lg bg-white border border-[#EFF0F6] text-[#4F46E5] hover:bg-[#EEF2FF] hover:border-[#C7D2FE] flex items-center justify-center transition-colors shrink-0"
                >
                  <Send className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ==================== GroupedDocDialog (invoice ou quote) ==================== */

function GroupedDocDialog({ open, kind, onClose, company, learners, initial, onCreated }) {
  const isQuote = kind === 'quote';
  const [selected, setSelected] = useState(new Set(initial.learner_ids));
  const [amount, setAmount] = useState(initial.amount_per_learner || 1200);
  const [tva, setTva] = useState(20);
  const [description, setDescription] = useState(initial.description || '');
  const [dueDate, setDueDate] = useState(initial.due_date || '');
  const [validityDays, setValidityDays] = useState(initial.validity_days || 30);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  if (!open) return null;

  const toggle = (id) => setSelected((s) => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const ht = (Number(amount) || 0) * selected.size;
  const tvaRate = Math.max(0, Math.min(100, Number(tva) || 0));
  const tvaAmount = Math.round(ht * tvaRate) / 100;
  const ttc = Math.round((ht + (ht * tvaRate / 100)) * 100) / 100;

  const submit = async () => {
    setError(null);
    if (selected.size === 0) { setError('Sélectionnez au moins un apprenant'); return; }
    if (!amount || Number(amount) <= 0) { setError('Montant invalide'); return; }
    setSaving(true);
    try {
      const payload = {
        learner_ids: Array.from(selected),
        amount_per_learner: Number(amount),
        tva_rate: tvaRate,
        description,
      };
      if (isQuote) {
        payload.validity_days = Number(validityDays) || 30;
      } else {
        payload.due_date = dueDate || null;
      }
      const endpoint = isQuote ? 'grouped-quote' : 'grouped-invoice';
      const { data } = await api.post(`/companies/${company.id}/billing/${endpoint}`, payload);
      onCreated(data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const dialogTitle = isQuote ? 'Devis groupé' : 'Facture groupée';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        data-testid={isQuote ? 'grouped-quote-dialog' : 'grouped-invoice-dialog'}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[520px] bg-white rounded-2xl shadow-xl"
      >
        <div className="px-6 py-5 border-b border-[#EFF0F6]">
          <div className="text-[11.5px] font-semibold uppercase tracking-wider text-[#8E8FA5]">{company.name}</div>
          <h3 className="text-[18px] font-bold text-[#1A1B2E] mt-0.5" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{dialogTitle}</h3>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="text-[12.5px] font-semibold text-[#4A4B63] block mb-1.5">Apprenants à facturer ({selected.size}/{learners.length})</label>
            <div className="max-h-[200px] overflow-y-auto space-y-1 border border-[#EFF0F6] rounded-xl p-2">
              {learners.map((l) => (
                <label key={l.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[#EEF2FF] cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid={`grouped-learner-${l.id}`}
                    checked={selected.has(l.id)}
                    onChange={() => toggle(l.id)}
                    className="w-4 h-4 accent-[#4F46E5]"
                  />
                  <span className="text-[12.5px] font-medium text-[#1A1B2E]">{l.firstName} {l.lastName}</span>
                  <span className="text-[11px] text-[#8E8FA5] ml-auto">{l.email}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[12.5px] font-semibold text-[#4A4B63] block mb-1.5">Montant HT / apprenant (€)</label>
              <input
                type="number" min="0" step="0.01"
                data-testid="grouped-amount-input"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full h-10 px-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] outline-none focus:bg-white focus:border-[#4F46E5]"
              />
            </div>
            <div>
              <label className="text-[12.5px] font-semibold text-[#4A4B63] block mb-1.5">TVA (%)</label>
              <input
                type="number" min="0" max="100" step="0.1"
                data-testid="grouped-tva-input"
                value={tva}
                onChange={(e) => setTva(e.target.value)}
                className="w-full h-10 px-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] outline-none focus:bg-white focus:border-[#4F46E5]"
              />
            </div>
            <div>
              <label className="text-[12.5px] font-semibold text-[#4A4B63] block mb-1.5">
                {isQuote ? 'Validité (jours)' : 'Échéance'}
              </label>
              {isQuote ? (
                <input
                  type="number" min="1" max="365"
                  data-testid="grouped-validity-input"
                  value={validityDays}
                  onChange={(e) => setValidityDays(e.target.value)}
                  className="w-full h-10 px-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] outline-none focus:bg-white focus:border-[#4F46E5]"
                />
              ) : (
                <input
                  type="date"
                  data-testid="grouped-due-input"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="w-full h-10 px-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] outline-none focus:bg-white focus:border-[#4F46E5]"
                />
              )}
            </div>
          </div>

          <div>
            <label className="text-[12.5px] font-semibold text-[#4A4B63] block mb-1.5">Description</label>
            <input
              data-testid="grouped-description-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={isQuote ? 'Offre de formation intra-entreprise…' : 'Formation intra-entreprise — Management Agile'}
              className="w-full h-10 px-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] outline-none focus:bg-white focus:border-[#4F46E5]"
            />
          </div>

          <div className="rounded-xl p-3 bg-[#EEF2FF] border border-[#C7D2FE] space-y-1.5">
            <div className="flex items-center justify-between text-[12.5px]">
              <span className="text-[#8E8FA5]">Sous-total HT</span>
              <span className="font-semibold text-[#1A1B2E]" data-testid="grouped-ht">
                {ht.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
              </span>
            </div>
            <div className="flex items-center justify-between text-[12.5px]">
              <span className="text-[#8E8FA5]">TVA ({tvaRate.toFixed(1)}%)</span>
              <span className="font-semibold text-[#1A1B2E]" data-testid="grouped-tva">
                {tvaAmount.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
              </span>
            </div>
            <div className="flex items-center justify-between pt-1.5 border-t border-[#C7D2FE]">
              <span className="text-[13px] font-semibold text-[#4A4B63]">Total TTC</span>
              <span className="text-[20px] font-bold text-[#4F46E5]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }} data-testid="grouped-total">
                {ttc.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
              </span>
            </div>
          </div>

          {error && <div className="text-[12px] text-[#B91C1C] bg-[#FEF2F2] border border-[#FECACA] rounded-lg px-3 py-2">{error}</div>}
        </div>
        <div className="px-6 py-4 border-t border-[#EFF0F6] flex justify-end gap-2">
          <button onClick={onClose} className="h-10 px-4 rounded-xl border border-[#EFF0F6] text-[13px] font-semibold text-[#4A4B63] hover:border-[#C7D2FE]">Annuler</button>
          <button
            data-testid="grouped-submit-btn"
            onClick={submit}
            disabled={saving || selected.size === 0 || !amount}
            className="h-10 px-5 rounded-xl brand-gradient text-white text-[13px] font-semibold disabled:opacity-60"
          >
            {saving ? 'Création…' : (isQuote ? 'Créer le devis' : 'Créer la facture')}
          </button>
        </div>
      </div>
    </div>
  );
}
